import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Badge, Button, Card, SectionCard } from "@/deps/ui-library";
import { useBankAccounts, useSaveSetupStep, useSetupStatus, useSetupSteps } from "@/api/queries";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/cn";
import { BankAccountsPage } from "@/features/banking/accounts/BankAccountsPage";
import { ApprovalRulesPage } from "@/features/settings/ApprovalRulesPage";
import { CompanyTab } from "@/features/settings/tabs/CompanyTab";
import { NumberingTab } from "@/features/settings/tabs/NumberingTab";
import { TaxCodesTab } from "@/features/settings/tabs/TaxCodesTab";
import { UsersTab } from "@/features/settings/tabs/UsersTab";
import { ChartStep, useChartStep, type ChartStepState } from "./ChartStep";

interface Step {
  key: string;
  label: string;
  why: string;
  body: (context: { chart: ChartStepState }) => ReactNode;
}

/**
 * One decision a step, on the same screens Settings has, so nothing is set up two ways. Every step but the
 * first can wait. The tax pack is configuration, not advice, and CompanyTab says so where it's applied.
 */
const STEPS: Step[] = [
  {
    key: "company",
    label: "Company and books",
    why: "The registered details on every document you send, then your tax pack, chart and current fiscal year.",
    body: () => <CompanyTab />,
  },
  {
    key: "chart",
    label: "Chart of accounts",
    why: "Every chart starts with the accounts Rigo posts to automatically. Add a suggested set on top, or build your own.",
    body: ({ chart }) => <ChartStep state={chart} />,
  },
  {
    key: "taxes",
    label: "Tax codes",
    why: "What the country pack configures. Check the rates with your accountant before relying on them.",
    body: () => <TaxCodesTab />,
  },
  {
    key: "approvals",
    label: "Approvals",
    why: "Who signs off a document before it posts. A company can turn any of these off.",
    body: () => <ApprovalRulesPage />,
  },
  {
    key: "banks",
    label: "Bank and cash",
    why: "The accounts money actually moves through. They become the choices on receipts and payments.",
    body: () => <BankAccountsPage />,
  },
  {
    key: "numbering",
    label: "Numbering",
    why: "The prefix and next number for each kind of document.",
    body: () => <NumberingTab />,
  },
  {
    key: "reporting",
    label: "Reporting",
    why: "Optional. Tags your accounts so statements read in a supplied format.",
    body: () => (
      <SectionCard title="Account mapping">
        <p className="text-fg-muted mb-3 text-sm">
          Standard statements work without this. Map your accounts when you want them in an
          IFRS-oriented or Nepal format.
        </p>
        <Link to="/reports/mapping" className="text-primary text-sm underline">
          Open account mapping
        </Link>
      </SectionCard>
    ),
  },
  {
    key: "team",
    label: "Your team",
    why: "Send people access now, or later from Settings.",
    body: () => <UsersTab />,
  },
];

function GettingStarted({ chart }: { chart: string }) {
  return (
    <SectionCard title="Your books are set up">
      <p className="text-fg-muted mb-3 text-sm">{`Chart of accounts: ${chart}.`}</p>
      <p className="text-fg-muted mb-3 text-sm">Next, the first things most companies do:</p>
      <ul className="flex flex-col gap-2 text-sm">
        <li>
          <Link to="/sales/customers" className="text-primary underline">
            Add your first customer
          </Link>
        </li>
        <li>
          <Link to="/sales/invoices/new" className="text-primary underline">
            Issue your first invoice
          </Link>
        </li>
        <li>
          <Link to="/settings/branding" className="text-primary underline">
            Set how your documents look
          </Link>
        </li>
      </ul>
      <div className="mt-4">
        <Link to="/" className="text-primary text-sm underline">
          Go to your overview
        </Link>
      </div>
    </SectionCard>
  );
}

/** Setting up a company, which can be left and picked up again from where it was. */
export function SetupWizardPage() {
  const status = useSetupStatus();
  const { data: recorded, isPending } = useSetupSteps();
  const { data: banks } = useBankAccounts();
  const save = useSaveSetupStep();
  const chart = useChartStep();
  const [chosen, setChosen] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  /** A step's real state counts, whether or not anyone pressed anything here. */
  const isDone = (key: string) => {
    if (recorded?.some((one) => one.step === key && one.status === "done")) return true;
    if (key === "company") {
      return !!status.data && Object.values(status.data).every(Boolean);
    }
    if (key === "banks") return (banks?.length ?? 0) > 0;
    return false;
  };
  const isLater = (key: string) =>
    !isDone(key) && !!recorded?.some((one) => one.step === key && one.status === "later");

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading</p>;

  // Back where it was left: the first step nobody has finished with or put off.
  const resume =
    STEPS.find((step) => !isDone(step.key) && !isLater(step.key))?.key ?? STEPS[0]!.key;
  const currentKey = chosen ?? resume;
  const index = STEPS.findIndex((step) => step.key === currentKey);
  const current = STEPS[index]!;
  const companyReady = isDone("company");
  const doneCount = STEPS.filter((step) => isDone(step.key)).length;

  const leave = async (stepStatus: "done" | "later") => {
    // The chart step writes the chosen set only now (handoff §5.6); Set up later keeps required accounts only.
    if (current.key === "chart" && stepStatus === "done") {
      try {
        await chart.commit();
      } catch {
        return; // The step shows why, and stays.
      }
    }
    save.mutate(
      { step: current.key, status: stepStatus },
      {
        onSuccess: () => {
          const next = STEPS[index + 1];
          if (next) setChosen(next.key);
          else setFinished(true);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Set up your company"
        subtitle={`${doneCount} of ${STEPS.length} done · every step but the first can wait.`}
      />
      {save.error ? (
        <Alert
          role="alert"
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(save.error as Error).message}
        />
      ) : null}
      {finished ? (
        <GettingStarted chart={chart.summary} />
      ) : (
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <Card size="none" className="shrink-0 overflow-hidden md:w-60">
            <nav aria-label="Setup steps">
              <ul className="flex flex-col">
                {STEPS.map((step, position) => (
                  <li key={step.key}>
                    <Button
                      variant="ghost"
                      colorScheme="default"
                      size="sm"
                      className={cn(
                        "h-auto w-full justify-start rounded-none px-3 py-2 text-left font-normal",
                        step.key === currentKey && "bg-muted font-medium",
                      )}
                      aria-current={step.key === currentKey ? "step" : undefined}
                      onClick={() => setChosen(step.key)}
                    >
                      <span className="flex w-full items-center justify-between gap-2">
                        <span>{`${position + 1}. ${step.label}`}</span>
                        {isDone(step.key) ? (
                          <Badge colorScheme="success" size="sm">
                            Done
                          </Badge>
                        ) : isLater(step.key) ? (
                          <Badge colorScheme="default" size="sm">
                            Later
                          </Badge>
                        ) : null}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </nav>
          </Card>
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <div>
              <h2 className="text-fg m-0 text-base font-semibold">{current.label}</h2>
              <p className="text-fg-muted m-0 text-sm">{current.why}</p>
            </div>
            {current.body({ chart })}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {index > 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setChosen(STEPS[index - 1]!.key)}
                >
                  Back
                </Button>
              ) : null}
              {current.key === "company" ? null : (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={save.isPending || chart.saving}
                  onClick={() => void leave("later")}
                >
                  Set up later
                </Button>
              )}
              <Button
                colorScheme="primary"
                variant="solid"
                size="sm"
                disabled={
                  save.isPending || chart.saving || (current.key === "company" && !companyReady)
                }
                onClick={() => void leave("done")}
              >
                {index === STEPS.length - 1 ? "Finish setup" : "Save and continue"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
